import { MIN_EVENT_MINUTES } from "../../../../../_shared/_logic/display-scale"
import { setTime } from "../../../../../../../../../../_shared/_logic/date-math"
import { buildFlexSlotFrames } from "../../../../../../../../../../_shared/_logic/flex-slots"
import type { BoardColumn, SoftSlot } from "../../../../../../../_logic/calendar-types"

type BuildSoftSlotsInput = {
  column: BoardColumn
  flexSlotsPerDay: number
  slotMinutes: number
  slotHeight: number
  viewStartMinutes: number
  viewEndMinutes: number
  nowTs: number
  includeUnmatchedSoftEvents?: boolean
}

const clampValue = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
const terminalStatuses = new Set(["CANCELED", "NOSHOW"])
const flexBlockerTypes = new Set(["ABSENCE", "INTERNAL_BLOCK"])

export const buildSoftSlots = ({
  column,
  flexSlotsPerDay,
  slotMinutes,
  slotHeight,
  viewStartMinutes,
  viewEndMinutes,
  nowTs,
  includeUnmatchedSoftEvents = false,
}: BuildSoftSlotsInput): SoftSlot[] => {
  const availableStart = column.availability?.startMinutes ?? viewStartMinutes
  const availableEnd = column.availability?.endMinutes ?? viewEndMinutes
  const dayOff = Boolean(column.availability?.dayOff)
  const workWindows =
    column.availability?.windows?.length && !dayOff
      ? column.availability.windows
      : [{ startMinutes: availableStart, endMinutes: availableEnd }]
  const flexFrames = buildFlexSlotFrames({
    workWindows,
    blockerWindows: [
      ...column.timedEvents.filter(isFlexBlockerEvent).map((event) => ({
        startMinutes: minutesFromDate(event.start),
        endMinutes: minutesFromDate(event.end),
      })),
      ...(column.externalBusyIntervals ?? []).map((interval) => ({
        startMinutes: minutesFromDate(interval.start),
        endMinutes: minutesFromDate(interval.end),
      })),
    ],
    flexSlotsPerDay,
    stepMinutes: MIN_EVENT_MINUTES,
  })
  const matchedSoftEvents = new Set<string>()
  const slots: SoftSlot[] = []

  for (const frame of flexFrames) {
    const snappedStartMinutes = frame.startMinutes
    const endMinutes = frame.endMinutes
    const startDate = setTime(
      column.day,
      Math.floor(snappedStartMinutes / 60),
      Math.round(snappedStartMinutes % 60),
    )
    const endDate = setTime(column.day, Math.floor(endMinutes / 60), Math.round(endMinutes % 60))
    const slotStartMs = startDate.getTime()
    const slotEndMs = endDate.getTime()
    const softEvent =
      column.timedEvents.find((event) => {
        if (event.event.flex_type !== "SOFT") return false

        const key = softEventKey(event)

        if (matchedSoftEvents.has(key)) return false

        const startMs = event.start?.getTime?.()

        if (Number.isFinite(startMs) && startMs >= slotStartMs && startMs < slotEndMs) return true

        return event.start < endDate && event.end > startDate
      }) ?? null

    if (softEvent) matchedSoftEvents.add(softEventKey(softEvent))

    const blocked = dayOff || !frame.available || Boolean(softEvent)
    const past = startDate.getTime() < nowTs
    const disabledReasons: string[] = []

    if (past) disabledReasons.push("Flexslot liegt in der Vergangenheit.")

    if (softEvent) disabledReasons.push("Dieser Flexslot ist bereits vergeben.")

    if (dayOff) disabledReasons.push("Dieser Tag ist als arbeitsfrei hinterlegt.")

    if (!frame.available) disabledReasons.push(disabledReasonForFrame(frame.reasonCode))

    const disabledReason = disabledReasons.length ? disabledReasons.join(" ") : ""
    const tooltip = softEvent ? "" : disabledReason
    const top = ((snappedStartMinutes - viewStartMinutes) / slotMinutes) * slotHeight
    const height = Math.max(
      slotHeight,
      ((endMinutes - snappedStartMinutes) / slotMinutes) * slotHeight,
    )

    slots.push({
      startMinutes: clampValue(snappedStartMinutes, availableStart, availableEnd),
      endMinutes,
      start: startDate,
      end: endDate,
      top,
      height,
      blocked,
      hasSoftEvent: Boolean(softEvent),
      softEvent,
      past,
      disabledReason,
      tooltip,
    })
  }

  if (includeUnmatchedSoftEvents) {
    slots.push(
      ...buildUnmatchedSoftEventSlots({
        column,
        matchedSoftEvents,
        viewStartMinutes,
        viewEndMinutes,
        slotMinutes,
        slotHeight,
        nowTs,
      }),
    )
  }

  return slots.sort(
    (a, b) => a.start.getTime() - b.start.getTime() || a.end.getTime() - b.end.getTime(),
  )
}

const minutesFromDate = (date: Date) => date.getHours() * 60 + date.getMinutes()

const softEventKey = (event: BoardColumn["timedEvents"][number]) =>
  event.event?.id ?? `${event.start.getTime()}:${event.end.getTime()}`

const dateAtMinutes = (day: Date, minutes: number) =>
  setTime(day, Math.floor(minutes / 60), Math.round(minutes % 60))

const minutesFromDayStart = (day: Date, date: Date) => {
  const dayStart = setTime(day, 0, 0)

  return (date.getTime() - dayStart.getTime()) / 60000
}

const buildUnmatchedSoftEventSlots = ({
  column,
  matchedSoftEvents,
  viewStartMinutes,
  viewEndMinutes,
  slotMinutes,
  slotHeight,
  nowTs,
}: {
  column: BoardColumn
  matchedSoftEvents: Set<string>
  viewStartMinutes: number
  viewEndMinutes: number
  slotMinutes: number
  slotHeight: number
  nowTs: number
}): SoftSlot[] => {
  const slots: SoftSlot[] = []
  for (const softEvent of column.timedEvents) {
    if (softEvent.event.flex_type !== "SOFT") continue

    if (matchedSoftEvents.has(softEventKey(softEvent))) continue

    const rawStartMinutes = minutesFromDayStart(column.day, softEvent.start)
    const rawEndMinutes = minutesFromDayStart(column.day, softEvent.end)
    const startMinutes = clampValue(rawStartMinutes, viewStartMinutes, viewEndMinutes)
    const endMinutes = clampValue(rawEndMinutes, viewStartMinutes, viewEndMinutes)

    if (
      !Number.isFinite(startMinutes) ||
      !Number.isFinite(endMinutes) ||
      endMinutes <= startMinutes
    )
      continue

    const start = dateAtMinutes(column.day, startMinutes)
    const end = dateAtMinutes(column.day, endMinutes)
    const past = start.getTime() < nowTs
    slots.push({
      startMinutes,
      endMinutes,
      start,
      end,
      top: ((startMinutes - viewStartMinutes) / slotMinutes) * slotHeight,
      height: Math.max(slotHeight, ((endMinutes - startMinutes) / slotMinutes) * slotHeight),
      blocked: true,
      hasSoftEvent: true,
      softEvent,
      past,
      disabledReason: past
        ? "Flexslot liegt in der Vergangenheit. Dieser Flexslot ist bereits vergeben."
        : "Dieser Flexslot ist bereits vergeben.",
      tooltip: "",
    })
  }

  return slots
}

const isFlexBlockerEvent = (event: BoardColumn["timedEvents"][number]) => {
  const type = String(event.event.event_type ?? "")
  const status = String(event.event.status ?? "")

  return flexBlockerTypes.has(type) && !terminalStatuses.has(status)
}

const disabledReasonForFrame = (reasonCode: string | null) => {
  if (reasonCode === "outside_working_time") {
    return "Mehr als die Hälfte liegt außerhalb der Arbeitszeit."
  }

  return "Mehr als die Hälfte ist durch Abwesenheit oder Blocker belegt."
}
